/**
 * Phone issues and checklists (docs/mobile-design.md Phase 2, chunk 3), shared by mobile web and React Native:
 * which building a new report starts on, what blocks sending it, how "My issues" is grouped and worded, and the
 * one-question-per-row checklist progress. Pure TS.
 */
import { INCIDENT_SEVERITIES, INCIDENT_STATUSES, isHsType, type Incident, type IncidentCategory, type IncidentStatus, type IncidentType } from '../ops/incidents';
import { evaluateItem, type ChecklistAnswer, type ChecklistItem, type ChecklistRun } from '../ops/checklists';
import { haversineMetres, NEAREST_BUILDING_MAX_M, type ClockLocation, type GeoReading } from '../workforce/clock';

// ------------------------------------------------------------------------------------------------ report: building
export type IssueBuildingSource = 'clocked_in' | 'shift' | 'nearby' | 'home' | 'only';
export interface IssueBuilding { location_id: string; source: IssueBuildingSource }
/** The line under the building on the report screen, so people can see why it was picked. */
export const ISSUE_BUILDING_HINT: Record<IssueBuildingSource, string> = {
  clocked_in: 'You’re clocked in here', shift: 'From your shift', nearby: 'You’re here (from your location)', home: 'Your home building', only: 'Your building',
};

/** The building with a position nearest to a usable reading, within NEAREST_BUILDING_MAX_M; else null. */
export function nearestBuilding(candidates: Pick<ClockLocation, 'id' | 'lat' | 'lng'>[], pos: GeoReading | null | undefined): string | null {
  if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lng) || pos.accuracy_m > NEAREST_BUILDING_MAX_M) return null;
  let best: { id: string; d: number } | null = null;
  for (const c of candidates) {
    if (c.lat == null || c.lng == null) continue;
    const d = haversineMetres(pos, { lat: c.lat, lng: c.lng });
    if (!best || d < best.d) best = { id: c.id, d };
  }
  return best && best.d <= NEAREST_BUILDING_MAX_M ? best.id : null;
}

/**
 * Where a phone report starts: the building you're clocked in at, else the one your current shift is at, else the
 * nearest by location (geofence), else your home building, else your only building. Only buildings you can report
 * at count. Null means you have to choose.
 */
export function issueBuilding(o: { locations: { id: string }[]; clockedIn?: string | null; shiftNow?: string | null; nearby?: string | null; home?: string | null }): IssueBuilding | null {
  const ok = (id: string | null | undefined): id is string => Boolean(id) && o.locations.some(l => l.id === id);
  if (ok(o.clockedIn)) return { location_id: o.clockedIn, source: 'clocked_in' };
  if (ok(o.shiftNow)) return { location_id: o.shiftNow, source: 'shift' };
  if (ok(o.nearby)) return { location_id: o.nearby, source: 'nearby' };
  if (ok(o.home)) return { location_id: o.home, source: 'home' };
  if (o.locations.length === 1) return { location_id: o.locations[0].id, source: 'only' };
  return null;
}

// ------------------------------------------------------------------------------------------------ report: fields
export const ISSUE_TITLE_MAX = 200;
export const ISSUE_PHOTO_MAX = 20;
/** The short type chips on the phone (the long labels are for the desktop form). */
export const ISSUE_TYPE_CHIPS: { key: IncidentType; label: string }[] = [
  { key: 'maintenance', label: 'Repair' }, { key: 'accident', label: 'Accident' }, { key: 'near_miss', label: 'Near miss' }, { key: 'other', label: 'Other' },
];
/** Categories offered for a type: hazard categories for accidents and near misses, repair ones otherwise. */
export function issueCategoryOptions(type: IncidentType, cats: Pick<IncidentCategory, 'id' | 'name' | 'kind' | 'archived_at' | 'sort_order'>[]) {
  const hs = isHsType(type);
  return cats.filter(c => !c.archived_at && (hs ? c.kind !== 'maintenance' : c.kind !== 'hs')).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}
/** Why Send is disabled, or null. Photos are encouraged but not required (a smell or a noise has no picture). */
export function issueReportBlocker(f: { title: string; location_id: string | null | undefined; uploading?: boolean }): string | null {
  if (f.uploading) return 'Waiting for photos to upload';
  if (!f.location_id) return 'Choose the building';
  if (!f.title.trim()) return 'Say what’s wrong';
  if (f.title.trim().length > ISSUE_TITLE_MAX) return `Keep it under ${ISSUE_TITLE_MAX} characters - add the rest as detail`;
  return null;
}
/** Accidents start as High; everything else as Medium. */
export const defaultIssueSeverity = (type: IncidentType) => (type === 'accident' ? 'high' : 'medium') as 'high' | 'medium';

// ------------------------------------------------------------------------------------------------ my issues
export const isOpenIssue = (s: IncidentStatus) => INCIDENT_STATUSES.find(x => x.key === s)?.open ?? true;
/** t699u: the issue is assigned to this person (any of its assignees; older rows: the one assignee). */
export const isIssueAssignee = (i: Pick<Incident, 'assignee_id'> & Partial<Pick<Incident, 'assignee_ids'>>, me: string | null | undefined) =>
  Boolean(me) && (i.assignee_ids ? i.assignee_ids.includes(me!) : i.assignee_id === me);
/** t699u/t700u: assignees in a list row, compactly: "Sam Reid", "Sam Reid +2", or null. */
export function issueAssigneesShort(i: Pick<Incident, 'assignee_id' | 'assignee_name'> & Partial<Pick<Incident, 'assignees'>>, me?: string | null): string | null {
  const list = i.assignees ?? (i.assignee_id ? [{ id: i.assignee_id, name: i.assignee_name ?? 'Someone' }] : []);
  if (!list.length) return null;
  const first = list.find(a => a.id === me) ? 'You' : list[0].name;
  return list.length > 1 ? `${first} +${list.length - 1}` : first;
}
export type IssueGroupKey = 'assigned' | 'reported' | 'following' | 'closed';
export const ISSUE_GROUP_LABEL: Record<IssueGroupKey, string> = { assigned: 'Assigned to you', reported: 'Reported by you', following: 'Following', closed: 'Resolved and closed' };
type IssueRow = Pick<Incident, 'id' | 'status' | 'reporter_id' | 'assignee_id' | 'updated_at'> & Partial<Pick<Incident, 'assignee_ids'>>;
/** "My issues": assigned to me first (I have to act), then ones I reported, then ones I follow; closed last. Newest activity first. */
export function groupMyIssues<T extends IssueRow>(list: T[], me: string): { key: IssueGroupKey; label: string; items: T[] }[] {
  const g: Record<IssueGroupKey, T[]> = { assigned: [], reported: [], following: [], closed: [] };
  for (const i of list) {
    const k: IssueGroupKey = !isOpenIssue(i.status) ? 'closed' : isIssueAssignee(i, me) ? 'assigned' : i.reporter_id === me ? 'reported' : 'following';
    g[k].push(i);
  }
  return (Object.keys(g) as IssueGroupKey[]).filter(k => g[k].length)
    .map(k => ({ key: k, label: ISSUE_GROUP_LABEL[k], items: [...g[k]].sort((a, b) => b.updated_at.localeCompare(a.updated_at)) }));
}
export type IssueTone = 'new' | 'active' | 'waiting' | 'done';
export function issueTone(s: IncidentStatus): IssueTone {
  return s === 'new' ? 'new' : s === 'on_hold' ? 'waiting' : isOpenIssue(s) ? 'active' : 'done';
}
export const issueStatusLabel = (s: IncidentStatus) => INCIDENT_STATUSES.find(x => x.key === s)?.label ?? s;
export const issueSeverityLabel = (s: string) => INCIDENT_SEVERITIES.find(x => x.key === s)?.label ?? s;
/** The second line of a row: "#4 · Cromwell Warehouse · aisle 14 · High". */
export function issueRowMeta(i: Pick<Incident, 'ref' | 'location_name' | 'location_detail' | 'severity'>): string {
  return [`#${i.ref}`, i.location_name, i.location_detail, issueSeverityLabel(i.severity)].filter(Boolean).join(' · ');
}

/** The latest-update line: a comment reads "Daniel: On my way"; a change reads "James reported this issue" (no colon, as on the desktop list).
 * `plain` turns mention tokens into names (the web passes usePlainText); line breaks collapse to one line. */
export function issueUpdateLine(u: NonNullable<Incident['latest_update']> | null | undefined, plain: (s: string) => string = s => s): string | null {
  if (!u) return null;
  const text = plain(u.text).replace(/\s+/g, ' ').trim();
  const first = u.actor_name?.trim().split(/\s+/)[0];
  if (!first) return text;
  return u.kind === 'comment' ? `${first}: ${text}` : `${first} ${text}`;
}

// ------------------------------------------------------------------------------------------------ checklists
export type ChecklistRowState = 'todo' | 'done' | 'failed';
export function checklistRowState(item: ChecklistItem, a: Pick<ChecklistAnswer, 'value' | 'photo_ids'> | undefined): ChecklistRowState {
  const r = evaluateItem(item, a?.value ?? undefined, a?.photo_ids.length ?? 0);
  return r.failed ? 'failed' : r.answered ? 'done' : 'todo';
}
export interface ChecklistProgress { done: number; total: number; failed: number; requiredLeft: number; percent: number; nextItemId: string | null }
/** Progress for the sticky bar: answered rows, failures, required rows left and the first unanswered row to jump to. */
export function checklistProgress(items: ChecklistItem[], answers: Pick<ChecklistAnswer, 'item_id' | 'value' | 'photo_ids'>[]): ChecklistProgress {
  const by = new Map(answers.map(a => [a.item_id, a]));
  let done = 0; let failed = 0; let requiredLeft = 0; let next: string | null = null;
  for (const it of items) {
    const st = checklistRowState(it, by.get(it.id));
    if (st !== 'todo') done++;
    if (st === 'failed') failed++;
    if (st === 'todo' && it.required) requiredLeft++;
    if (st === 'todo' && !next) next = it.id;
  }
  return { done, total: items.length, failed, requiredLeft, percent: items.length ? Math.round((done / items.length) * 100) : 0, nextItemId: next };
}
/** The photo button on a row: a photo question says "Take photo" until it has one; any row can add evidence. */
export function photoButtonLabel(item: Pick<ChecklistItem, 'type'>, count: number): string {
  if (item.type === 'photo') return count ? 'Add another' : 'Take photo';
  return count ? 'Add photo' : 'Photo';
}
/** Phone "My checklists": to do now, later today, and finished. Missed runs that can still be done count as to do. */
export function groupMyChecklists<T extends Pick<ChecklistRun, 'status' | 'due_from' | 'due_by'>>(runs: T[], now = Date.now()) {
  const todo: T[] = []; const later: T[] = []; const done: T[] = [];
  for (const r of runs) {
    if (r.status === 'completed') done.push(r);
    else if (r.due_from && new Date(r.due_from).getTime() > now) later.push(r);
    else todo.push(r);
  }
  return [{ key: 'todo', label: 'To do now', items: todo }, { key: 'later', label: 'Later today', items: later }, { key: 'done', label: 'Done', items: done }].filter(g => g.items.length);
}
